import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { fetchAuthSession } from "aws-amplify/auth";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Extract userToken (Cognito user ID) from JWT token
 * @returns Promise<string> - The userToken (Cognito sub claim)
 * @throws Error if no token is available or extraction fails
 */
export async function getUserTokenFromJWT(): Promise<string> {
  try {
    const idToken = await getIdTokenFromSession();
    if (!idToken) {
      throw new Error("No authentication token available");
    }
    // Decode JWT token to get userId (sub claim)
    const payload = JSON.parse(atob(idToken.split(".")[1]));
    return payload.sub;
  } catch (error) {
    console.error("Error extracting userToken from token:", error);
    throw new Error("Failed to get user ID from authentication token");
  }
}

/**
 * Extract the raw ID token from the current auth session
 * @returns Promise<string | null> - The ID token or null if unavailable
 */
export async function getIdTokenFromSession(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (error) {
    console.error("Error fetching auth session:", error);
    return null;
  }
}

/**
 * Handles token limit exceeded errors from API responses
 * Transforms 403 errors with token limit exceeded into enhanced errors with token status
 * @param error - The error from axios/API call
 * @returns The original error if not a token limit error, or an enhanced error with token status
 */
export function handleTokenLimitError(error: any): Error {
  if (
    error.response?.status === 403 &&
    error.response?.data?.error === "Token limit exceeded"
  ) {
    const enhancedError = new Error(
      error.response.data.message || "Token limit exceeded"
    );
    (enhancedError as any).status = 403;
    (enhancedError as any).tokenStatus = error.response.data.tokenStatus;
    (enhancedError as any).response = error.response;
    return enhancedError;
  }
  return error;
}

/**
 * Checks if an error is a timeout or network error that should allow WebSocket processing to continue
 * These errors occur when HTTP requests timeout but the backend continues processing via WebSocket
 * @param error - The error from axios/API call
 * @returns true if the error is a timeout/network error that should allow WebSocket to continue
 */
export function isWebSocketContinueError(error: any): boolean {
  if (!error) return false;

  // Check for timeout flags set by API interceptor
  if (error.isTimeout || error.is504) {
    return true;
  }

  // Check for 504 Gateway Timeout
  if (error.response?.status === 504) {
    return true;
  }

  // Check for timeout error codes
  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
    return true;
  }

  // Check for network errors (ERR_NETWORK) - these can occur when HTTP times out but WebSocket continues
  // This includes CORS errors that happen during long-running requests
  if (error.code === "ERR_NETWORK" || error.code === "ERR_FAILED") {
    return true;
  }

  // Check for network errors that might indicate timeout
  if (
    error.message?.includes("timeout") ||
    error.message?.includes("504") ||
    error.message?.includes("Network Error")
  ) {
    return true;
  }

  // Check if request was made but no response (timeout scenario)
  // This includes CORS preflight failures that can happen with long requests
  if (error.request && !error.response) {
    return true;
  }

  return false;
}

/**
 * Checks if an error or response contains compilation errors from test generation
 * @param errorOrResponse - The error from axios/API call or WebSocket message response
 * @returns true if it's a compilation error response
 */
export function isCompilationErrorResponse(errorOrResponse: any): boolean {
  // Check HTTP error response format
  if (
    errorOrResponse?.response?.status === 400 &&
    errorOrResponse?.response?.data?.error === "Compilation failed"
  ) {
    return true;
  }

  // Check WebSocket message format
  if (
    errorOrResponse?.error === "Compilation failed" &&
    errorOrResponse?.compilationErrors
  ) {
    return true;
  }

  return false;
}

/**
 * Extracts compilation errors from an error response or WebSocket message
 * @param errorOrResponse - The error from axios/API call or WebSocket message response
 * @returns The compilation errors string
 */
export function extractCompilationErrors(errorOrResponse: any): string {
  // Handle HTTP error response format
  if (errorOrResponse?.response?.data) {
    const errorData = errorOrResponse.response.data;
    return (
      errorData.compilationErrors ||
      errorData.warnings?.join("\n") ||
      "Contract code does not compile"
    );
  }

  // Handle WebSocket message format
  if (errorOrResponse?.compilationErrors) {
    return (
      errorOrResponse.compilationErrors ||
      errorOrResponse.warnings?.join("\n") ||
      "Contract code does not compile"
    );
  }

  return "Contract code does not compile";
}

/**
 * Gets the normalized site root path, removing trailing slashes
 * @returns The normalized site root path (e.g., "/chat")
 */
export function getSiteRoot(): string {
  return (import.meta.env.VITE_SITE_ROOT || "/chat").replace(/\/$/, "");
}

/**
 * Builds a path relative to the site root
 * @param path - The path to append (e.g., "signin", "/signin", "signup")
 * @returns The full normalized path (e.g., "/chat/signin")
 */
export function getSitePath(path: string): string {
  const siteRoot = getSiteRoot();
  // Remove leading slash from path if present to avoid double slashes
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  return `${siteRoot}/${normalizedPath}`;
}

/**
 * Downloads a base64 encoded string as a file
 * @param base64 - The base64 encoded string
 * @param filename - The filename to save as
 */
export function downloadBase64File(base64: string, filename: string) {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let i = 0; i < byteCharacters.length; i++) {
    byteArrays.push(byteCharacters.charCodeAt(i));
  }

  const blob = new Blob([new Uint8Array(byteArrays)], {
    type: "application/zip",
  });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export interface RecentProject {
  id: string;
  name: string;
  hasContract: boolean;
}

export function getRecentProjects(userId?: string | null): RecentProject[] {
  if (typeof window === 'undefined') return [];
  const key = userId ? `recentProjects_${userId}` : 'recentProjects';
  const stored = localStorage.getItem(key);
  try {
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    return [];
  }
}

export function addRecentProject(project: RecentProject, userId?: string | null) {
  if (typeof window === 'undefined') return;
  const recent = getRecentProjects(userId);
  const filtered = recent.filter(p => p.id !== project.id);
  const updated = [project, ...filtered].slice(0, 10);
  const key = userId ? `recentProjects_${userId}` : 'recentProjects';
  localStorage.setItem(key, JSON.stringify(updated));
  // Also keep legacy for compatibility if needed
  localStorage.setItem('lastProjectId', project.id);
  localStorage.setItem('lastProjectName', project.name);
}

